/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Checkbox,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Space,
  Text,
  useColorMode,
  useColorStyle,
  Select,
  Option,
  Box,
} from '@tonic-one/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormInput,
  FormLabel,
  FormTextarea,
} from 'src/components/Common/FormComponent';
import { useWhyDidYouUpdate } from 'src/utils/hooks';
import isEqual from 'lodash/isEqual';
import i18next from 'i18next';
import {
  storeSelector,
  templateSelector,
  usePolicyContext,
} from '@Policy/store';
import { actionTranslateMap } from '@Policy/AdvancedThreatProtection/PolicyTemplate/Components/shared';
import { ActionEnum, DlpPolicyItem } from '@Policy/utils/types';
import { CAS_DOC_LINK, getCasOLHLink } from '@Utils/casDocLink';
import { mipLabelsSelector } from '@Policy/store/filters/dlp';

const DlpAdvancedOptionModal = (props: {
  replacementTextDesc: string;
  isVisible: boolean;
  onClose: () => void;
  availableTokenShow: boolean;
  replaceFileNameShow: boolean;
  mipFeatureShow: boolean;
  syncLabels?: () => void;
  config: {
    replaceFileName: string;
    replaceText: string;
    replaceTextDefault: string;
  };
  saveConfig: (config: {
    replaceFileName: string;
    replaceText: string;
  }) => void;
}) => {
  const {
    replacementTextDesc,
    isVisible,
    onClose,
    config: { replaceFileName, replaceText, replaceTextDefault },
    saveConfig,
    mipFeatureShow,
    syncLabels,
    replaceFileNameShow = true,
    availableTokenShow = true,
  } = props;
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  const [fileName, setFileName] = React.useState(replaceFileName);
  const [text, setText] = React.useState(replaceText);
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(contextStore)<DlpPolicyItem>;
  const setDpState = contextSelector((s) => s.setDpState);

  const dpMipOverrideEnabled = policySelector(
    (state) => state.tmDpfilters.dpMipOverrideEnabled
  );
  const dpMipBackupEnabled = policySelector(
    (state) => state.tmDpfilters.dpMipBackupEnabled
  );
  const dpMipBackupAction = policySelector(
    (state) => state.tmDpfilters.dpMipBackupAction
  );

  const dpMipLabelId = policySelector(
    (state) => state.tmDpfilters.dpMipLabelId
  );
  const mipLabels = contextSelector(mipLabelsSelector, isEqual);

  const onReset = React.useCallback(() => {
    setText(replaceTextDefault);
  }, [replaceTextDefault]);
  const onSave = React.useCallback(() => {
    const config = {
      replaceFileName: fileName,
      replaceText: text,
    };
    saveConfig(config);
    onClose();
  }, [fileName, text]);

  useWhyDidYouUpdate('MalwareScanning AdvancedOptionModal', {
    ...props,
    text,
    fileName,
  });

  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={isVisible}
      onClose={onClose}
      size="lg"
    >
      <ModalOverlay opacity={0.9} />
      <ModalContent backgroundColor={colorStyle.background.secondary}>
        {/*<ModalHeader>{t('policy.action.advanced.titleForMalware')}</ModalHeader>*/}
        <ModalHeader></ModalHeader>

        <ModalBody ml="24px" mt="16px" mb="16px">
          <Flex direction="column">
            <Flex mb="16px">
              <Text>{replacementTextDesc}</Text>
            </Flex>
            {replaceFileNameShow && (
              <Flex mb="16px">
                <FormLabel label={t('policy.action.replaceAction.filename')} />
                <FormInput
                  placeholder={t('policy.action.replaceAction.filenameInput')}
                  value={fileName}
                  width="600px"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    console.log(e.target.value);
                    setFileName(e.target.value);
                  }}
                />
              </Flex>
            )}
            <Flex mb="16px">
              <FormLabel label={t('policy.action.replaceAction.text')} />
              <FormTextarea
                value={text}
                width="600px"
                onChange={(e) => {
                  setText(e.target.value);
                }}
              />
            </Flex>
            <Flex mb="16px">
              <FormLabel label="" />
              <Button onClick={() => onReset()}>
                {t('common.label.reset')}
              </Button>
              {availableTokenShow && (
                <Link
                  width="100%"
                  href={getCasOLHLink(
                    CAS_DOC_LINK.policy.atp.tokenList,
                    i18next.language
                  )}
                  target="_blank"
                >
                  <Text
                    ml={12}
                    overflow="hidden"
                    whiteSpace="nowrap"
                    textOverflow="ellipsis"
                  >
                    {t('policy.label.availableTokens')}
                  </Text>
                </Link>
              )}
            </Flex>
            {mipFeatureShow && (
              <Box>
                <Flex mt={12}>
                  <Text mb={12}>
                    {t('policy.action.sensitivityLabels.text')}
                  </Text>
                </Flex>
                <Flex flexDirection="row">
                  <Text
                    mb={12}
                    whiteSpace="pre-wrap"
                    color={colorStyle.color.secondary}
                  >
                    {t('policy.action.sensitivityLabels.text.desc')}
                    <Link
                      ml={8}
                      href={getCasOLHLink(
                        CAS_DOC_LINK.provision.mip.instruction,
                        i18next.language
                      )}
                      target="_blank"
                    >
                      {t(
                        'policy.action.sensitivityLabels.supportedFileTypes.text'
                      )}
                    </Link>
                  </Text>
                </Flex>
                <Flex mb="16px" flexDirection="row">
                  <Select
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      let dpMipLabelName = mipLabels.find(
                        (item) => item.labelId === e.target.value
                      )?.labelName;
                      if (!dpMipLabelName) {
                        dpMipLabelName = '';
                      }
                      setDpState({
                        dpMipLabelId: e.target.value,
                        dpMipLabelName,
                      });
                    }}
                    width="206px"
                    mr="12px"
                    value={dpMipLabelId}
                  >
                    {mipLabels.length === 0 && (
                      <Option value={''} key={''}>
                        {t('policy.mipLabel.noMipLabel')}
                      </Option>
                    )}
                    {mipLabels.length > 0 && (
                      <Option value={''} key={''}>
                        {t('policy.mipLabel.selectedMipLabel')}
                      </Option>
                    )}
                    {mipLabels.length > 0 &&
                      mipLabels.map((item) => {
                        return (
                          <Option value={item.labelId} key={item.labelId}>
                            {item.labelName}
                          </Option>
                        );
                      })}
                  </Select>
                  <Button onClick={syncLabels}>
                    {t('policy.mipLabel.syncLabel')}
                  </Button>
                </Flex>
                <Flex align="center" mb="8px">
                  <Checkbox
                    checked={!!dpMipOverrideEnabled}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setDpState({
                        dpMipOverrideEnabled: e.target.checked ? 1 : 0,
                      });
                    }}
                  >
                    <Text lineHeight="20px" fontSize="14px">
                      {t('policy.action.sensitivityLabels.override')}
                    </Text>
                  </Checkbox>
                </Flex>
                <Flex flexDirection="row">
                  <Checkbox
                    checked={!!dpMipBackupEnabled}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setDpState({
                        dpMipBackupEnabled: e.target.checked ? 1 : 0,
                      });
                    }}
                  >
                    <Flex align="center" fontSize="14px">
                      {t('policy.action.sensitivityLabels.takeBackupAction')}
                      <Select
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          setDpState({
                            dpMipBackupAction: Number(e.target.value),
                          });
                        }}
                        width="206px"
                        mr="12px"
                        value={dpMipBackupAction}
                        ml={8}
                      >
                        <Option value={ActionEnum.pass} key={ActionEnum.pass}>
                          {t(actionTranslateMap[ActionEnum.pass])}
                        </Option>
                        <Option
                          value={ActionEnum.delete}
                          key={ActionEnum.delete}
                        >
                          {t(actionTranslateMap[ActionEnum.delete])}
                        </Option>
                        <Option
                          value={ActionEnum.quarantine}
                          key={ActionEnum.quarantine}
                        >
                          {t(actionTranslateMap[ActionEnum.quarantine])}
                        </Option>
                      </Select>
                    </Flex>
                  </Checkbox>
                </Flex>
              </Box>
            )}
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" minWidth="20x" onClick={onSave}>
            {t('common.button.save')}
          </Button>
          <Space width="2x" />
          <Button minWidth="20x" onClick={onClose}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default React.memo(DlpAdvancedOptionModal);
